import { SectionHeader } from "../../SectionHeader";
import { ModuleGrid } from "../../../layouts/ModuleGrid";
import { useIsPortraitMobile } from "../../../hooks/useIsPortraiteMobile";
import TiltMessage from "../../../components/feedback/TiltMessage";
import { useState, useMemo } from "react";
import { CalendarEvents } from "../components/calendarEvents";
import type { CalendarEvent } from "../../../types/types";
import { EventsCard } from "../components/eventsCard";
import { useGetEventMonth } from "../hooks/useGetEventMonth";
import { useGetRetailEventMonth } from "../hooks/useGetRetailEvent";
import { useGetWholesaleEventMonth } from "../hooks/useGetWholesaleEvent";
import { useGetEcomEventMonth } from "../hooks/useGetEcomEvent";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../ui/dropdown-menu";

export function EventsDashboard() {
  const showTiltMessage = useIsPortraitMobile();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [eventSource, setEventSource] = useState<"data" | "retail" | "wholesale" | "ecom">("ecom");

  // Get current month in "YYYY-MM" format
  const month = useMemo(() => {
    const d = selectedDate ?? new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [selectedDate]);

  const { data } = useGetEventMonth(month);
  const { data: retailEvent } = useGetRetailEventMonth(month);
  const { data: wholesaleEvent } = useGetWholesaleEventMonth(month);
  const { data: ecomEvent } = useGetEcomEventMonth(month);

  // Helper to flatten API response to CalendarEvent[]
  function flattenEvents(source: any): CalendarEvent[] {
    if (!source) return [];
    return Object.values(source)
      .flat()
      .map((evt: any) => ({
        id: evt.id.toString(),
        title: evt.title,
        description: evt.description,
        source: ["meta", "manual", "clayrio"].includes(evt.source)
          ? (evt.source as "meta" | "manual" | "clayrio")
          : "manual",
        channel: ["ecommerce", "retail", "wholesale"].includes(evt.channel)
          ? (evt.channel as "ecommerce" | "retail" | "wholesale")
          : "ecommerce",
          location: evt.location || "",
        startAt: evt.start_at,
        endAt: evt.end_at,
      }));
  }

  // Choose events based on selected source
  const events: CalendarEvent[] = useMemo(() => {
    switch (eventSource) {
      case "data":
        return flattenEvents(data);
      case "retail":
        return flattenEvents(retailEvent);
      case "wholesale":
        return flattenEvents(wholesaleEvent);
      case "ecom":
        return flattenEvents(ecomEvent);
      default:
        return [];
    }
  }, [eventSource, data, retailEvent, wholesaleEvent, ecomEvent]);

  return (
    <>
      <TiltMessage show={showTiltMessage} />
      <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
        <SectionHeader title="Key Events" />
        <div className="mb-4">
          <DropdownMenu>
            <DropdownMenuTrigger className="px-4 py-2 bg-muted rounded">
              {eventSource === "data" && "All"}
              {eventSource === "retail" && "Retail"}
              {eventSource === "wholesale" && "Wholesale"}
              {eventSource === "ecom" && "Ecommerce"}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {/* <DropdownMenuItem onClick={() => setEventSource("data")}>All</DropdownMenuItem> */}
              <DropdownMenuItem onClick={() => setEventSource("retail")}>Retail</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEventSource("wholesale")}>Wholesale</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEventSource("ecom")}>Ecommerce</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <ModuleGrid>
          <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
            <CalendarEvents
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              events={events}
            />
          </div>
          <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
            <EventsCard
              events={events}
              selectedDate={selectedDate}
              onEventsChange={() => {}}
              eventSource={eventSource}
            />
          </div>
        </ModuleGrid>
      </div>
    </>
  );
}
